/**
 * ALTIORA · IQ Score — la conscience de soi du système.
 * ---------------------------------------------------------------
 * Dashboard interne qui affiche la santé globale d'ALTIORA sur 7
 * dimensions, avec les zones de progression prioritaires.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Brain, RefreshCw, Gauge, Sparkles, Sprout, Activity,
  Radio, Focus, Award, Target,
} from "lucide-react";
import {
  PageShell, PageHeader, Card, PrimaryButton, GhostButton, Loader,
  NAVY, CHAMPAGNE, IVORY, SLATE, NAVY_DARK as INK,
} from "@/components/altiora/PremiumUI";
import { TopBar } from "@/components/layout/TopBar";
import { api } from "@/lib/api";

const DIMENSION_META = {
  knowledge_coverage: { icon: Brain,     label: "Couverture des connaissances",
                        desc: "Taille du patrimoine par rapport à la cible long terme." },
  freshness:          { icon: Sprout,    label: "Fraîcheur des données",
                        desc: "% d'items révisés dans les 90 derniers jours." },
  citation_quality:   { icon: Award,     label: "Qualité des citations",
                        desc: "% d'items citant une source vérifiable + poids réglementaire." },
  recommendation_quality: { icon: Target, label: "Qualité des recommandations",
                        desc: "Follow-rate × success-rate mesurés via Decision Memory." },
  learning_maturity:  { icon: Sparkles,  label: "Maturité d'apprentissage",
                        desc: "Niveau moyen d'apprentissage sur l'ensemble des utilisateurs actifs." },
  graph_health:       { icon: Activity,  label: "Santé du graphe",
                        desc: "Absence d'orphelins et d'incohérences dans le graphe de connaissances." },
  ingestion_velocity: { icon: Radio,     label: "Vitesse d'ingestion",
                        desc: "Nombre de jobs KIC créés dans les 30 derniers jours." },
};

const BAND_COLORS = {
  excellent:   "#0a7f42",
  "avancé":    "#527a41",
  adapté:      "#c9a34e",
  précoce:     "#8B4E00",
  exploration: "#c95050",
};

export default function AdminAltioraIQ() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (force = false) => {
    force ? setRefreshing(true) : setBusy(true);
    try {
      const [scoreRes, liveRes] = await Promise.all([
        api.get(`/altiora-iq/score${force ? "?force_refresh=true" : ""}`),
        api.get("/altiora-iq/live-metrics"),
      ]);
      setData(scoreRes.data);
      setLive(liveRes.data);
      if (force) toast.success("IQ Score recalculé");
    } catch { toast.error("Impossible de charger le score"); }
    finally { setBusy(false); setRefreshing(false); }
  };

  useEffect(() => { load(false); }, []);

  if (busy) return (
    <PageShell><TopBar />
      <div className="max-w-6xl mx-auto py-16 flex justify-center"><Loader size={26} /></div>
    </PageShell>
  );

  if (!data) return null;
  const dims = data.dimensions || {};
  const focus = data.priority_focus || [];

  return (
    <PageShell>
      <TopBar />
      <div className="max-w-[1400px] mx-auto px-6 py-8" data-testid="admin-iq-root">
        <PageHeader
          eyebrow="ALTIORA · Administration"
          title="Score d'intelligence ALTIORA"
          subtitle="Conscience de soi du système. Score composite sur 7 dimensions, mis à jour quotidiennement."
          testid="admin-iq-header"
        />

        {/* Header : score global + focus prioritaire */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
          <Card className="lg:col-span-1 p-6" testid="admin-iq-global">            <div className="text-[10px] font-mono uppercase tracking-widest"
                 style={{ color: SLATE }}>
              Score global — {data.date}
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-5xl font-serif" style={{ color: NAVY }}>
                {data.global_score}
              </span>
              <span className="text-lg" style={{ color: SLATE }}>/ 100</span>
            </div>
            <div className="mt-2 inline-block text-[11px] font-mono uppercase tracking-widest px-2 py-0.5 rounded"
                 style={{ background: `${BAND_COLORS[data.global_band] || SLATE}22`,
                          color: BAND_COLORS[data.global_band] || NAVY }}>
              {data.global_band}
            </div>
            <div className="mt-4 h-2 rounded-full overflow-hidden"
                 style={{ background: "#efe7cf" }}>
              <div className="h-full transition-all duration-700"
                   style={{ width: `${data.global_score}%`,
                            background: CHAMPAGNE }} />
            </div>
            <div className="mt-4">
              <GhostButton onClick={() => load(true)} disabled={refreshing}
                            testid="admin-iq-refresh">
                <RefreshCw size={12} /> {refreshing ? "Calcul…" : "Recalculer"}
              </GhostButton>
            </div>
          </Card>

          <Card className="lg:col-span-2 p-6" testid="admin-iq-focus">
            <div className="flex items-center gap-2 mb-2">
              <Focus size={14} style={{ color: CHAMPAGNE }} />
              <h3 className="font-serif text-sm" style={{ color: NAVY }}>
                Priorités d'amélioration détectées par le système
              </h3>
            </div>
            <ul className="mt-2 space-y-2">
              {focus.map((f) => {
                const meta = DIMENSION_META[f.dimension] || {};
                const Icon = meta.icon || Gauge;
                return (
                  <li key={f.dimension} className="flex items-center gap-3 p-3 rounded"
                       style={{ background: "#faf6ea", border: "1px solid #e5d9b8" }}
                       data-testid={`admin-iq-focus-${f.dimension}`}>
                    <Icon size={16} style={{ color: BAND_COLORS[f.band] || NAVY }} />
                    <div className="flex-1">
                      <div className="text-sm" style={{ color: NAVY }}>
                        {meta.label || f.dimension}
                      </div>
                      <div className="text-[11px]" style={{ color: SLATE }}>
                        {meta.desc}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-serif" style={{ color: NAVY }}>
                        {f.score}
                      </div>
                      <div className="text-[10px] font-mono uppercase"
                           style={{ color: BAND_COLORS[f.band] || SLATE }}>
                        {f.band}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="mt-3 text-[11px]" style={{ color: SLATE }}>
              Le système sait où il doit encore progresser. Ces zones bénéficient
              en priorité de nouvelles ingestions, révisions et connexions.
            </div>
          </Card>
        </div>

        {/* Grille des 7 dimensions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mt-6">
          {Object.entries(dims).map(([key, dim]) => (
            <DimensionCard key={key} dkey={key} dim={dim} />
          ))}
        </div>

        {/* Intelligence Progress Dashboard · métriques temps réel */}
        {live && (
          <div className="mt-8" data-testid="admin-iq-live">
            <h2 className="font-serif text-lg mb-3" style={{ color: NAVY }}>
              Progression de l'intelligence — temps réel
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <MetricTile label="Total items" value={live.patrimoine.total_items}
                            hint={`+${live.patrimoine.new_today} aujourd'hui`}
                            testid="admin-iq-metric-items" />
              <MetricTile label="Arêtes" value={live.patrimoine.total_edges}
                            testid="admin-iq-metric-edges" />
              <MetricTile label="Confiance moyenne"
                            value={`${live.patrimoine.avg_confidence}%`}
                            testid="admin-iq-metric-conf" />
              <MetricTile label="Nouvelles / 7j"
                            value={live.patrimoine.new_week}
                            testid="admin-iq-metric-new-week" />
              <MetricTile label="Jobs KIC / 24h"
                            value={live.ingestion.jobs_24h}
                            hint={`${live.ingestion.jobs_pending_review} à valider`}
                            testid="admin-iq-metric-jobs-24h" />
              <MetricTile label="Erreurs KIC / 7j"
                            value={live.ingestion.jobs_failed_week}
                            color={live.ingestion.jobs_failed_week > 0 ? "#c95050" : NAVY}
                            testid="admin-iq-metric-errors" />
              <MetricTile label="Recos suivies / 7j"
                            value={live.recommendations.followed_week}
                            hint={`${live.recommendations.succeeded_week} réussies`}
                            testid="admin-iq-metric-followed" />
              <MetricTile label="Événements / 24h"
                            value={live.timeline_events_24h}
                            testid="admin-iq-metric-events" />
            </div>
            <div className="mt-2 text-[10px]" style={{ color: SLATE }}>
              Généré le {new Date(live.generated_at).toLocaleString("fr-CA")}.
              {live.watcher.last_source && (
                <> Dernière source watchée : {live.watcher.last_source}
                {live.watcher.last_checked_at && ` (${new Date(live.watcher.last_checked_at).toLocaleString("fr-CA")})`}.</>
              )}
            </div>
          </div>
        )}
      </div>
    </PageShell>
  );
}

function MetricTile({ label, value, hint, color, testid }) {
  return (
    <Card className="p-3" testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-mono"
           style={{ color: SLATE }}>{label}</div>
      <div className="text-2xl font-serif mt-1"
           style={{ color: color || NAVY }}>
        {typeof value === "number" ? value.toLocaleString("fr-CA") : value}
      </div>
      {hint && (
        <div className="text-[10px] mt-1" style={{ color: CHAMPAGNE }}>{hint}</div>
      )}
    </Card>
  );
}

function DimensionCard({ dkey, dim }) {
  const meta = DIMENSION_META[dkey] || {};
  const Icon = meta.icon || Gauge;
  const bandColor = BAND_COLORS[dim.band] || SLATE;
  return (
    <Card className="p-4" testid={`admin-iq-dim-${dkey}`}>
      <div className="flex items-start gap-2">
        <div className="w-9 h-9 rounded-full flex items-center justify-center"
             style={{ background: `${bandColor}18`,
                      border: `1px solid ${bandColor}66` }}>
          <Icon size={14} style={{ color: bandColor }} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-serif" style={{ color: NAVY }}>
            {meta.label || dkey}
          </div>
          <div className="text-[10px] font-mono uppercase mt-0.5"
               style={{ color: bandColor }}>
            {dim.band}
          </div>
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-3xl font-serif" style={{ color: NAVY }}>
          {dim.score}
        </span>
        <span className="text-xs" style={{ color: SLATE }}>/ 100</span>
      </div>
      <div className="mt-2 h-1.5 rounded-full overflow-hidden"
           style={{ background: "#efe7cf" }}>
        <div className="h-full transition-all duration-500"
             style={{ width: `${dim.score}%`, background: bandColor }} />
      </div>
      <div className="mt-3 text-[10px] space-y-1" style={{ color: SLATE }}>
        {Object.entries(dim)
          .filter(([k]) => !["score", "band"].includes(k))
          .slice(0, 4)
          .map(([k, v]) => (
            <div key={k} className="flex justify-between">
              <span>{k}</span>
              <span style={{ color: NAVY }}>
                {typeof v === "number" ? v.toLocaleString("fr-CA") :
                 typeof v === "boolean" ? String(v) :
                 typeof v === "object" ? "" : String(v).slice(0, 30)}
              </span>
            </div>
          ))}
      </div>
    </Card>
  );
}
